/**
 * 권한 축과 **라우트별 요구 권한 표** (지시54 ③, 결정211).
 *
 * 설계 근거는 `docs/07-org-authz.md`. 여기서 지키는 규칙은 하나다 —
 * **라우트가 60개다. 눈으로 훑지 않는다.**
 *
 * 표에 없는 라우트가 있으면 `tests/integration/route-permission-policy.test.ts`
 * 가 이름을 대며 깨진다. 새 라우트가 판단 없이 들어오지 못한다.
 *
 * `'none'` 도 **명시적으로** 적는다. 빠진 것과 일부러 열어 둔 것은 다르고,
 * 그 구분이 없으면 표가 방어를 못 한다.
 */

/** 운영자가 요구한 축. 범용 RBAC 가 아니다 — 축이 늘면 그때 늘린다. */
export const PERMISSIONS = [
  'pipeline.read',
  'pipeline.create',
  'pipeline.update',
  'pipeline.delete',
  'access.manage',
  'member.manage',
  'admin',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/** 인증만 되면 되는 라우트. 권한 축과 무관하다. */
export const NO_PERMISSION = 'none';

export interface RoutePermission {
  /** 이 라우트를 부르려면 있어야 하는 권한. `admin` 은 언제나 통과한다. */
  require: Permission | typeof NO_PERMISSION;
  /** 왜 그렇게 정했는지. 이 문장이 곧 검토 기록이다. */
  reason: string;
}

/**
 * `METHOD /path` → 요구 권한.
 *
 * 되풀이해서 판단이 갈린 자리 둘을 먼저 적어 둔다.
 *
 * **① 목록 읽기와 세팅하기를 갈랐다.** 자격증명·노드·배포 대상은 **만들고 고치는 것**이
 * `access.manage` 지만, **목록을 보는 것**은 `pipeline.read` 다. 파이프라인을 짜려면
 * 어떤 자격증명·노드를 고를지 보여야 하는데, 그것까지 `access.manage` 로 막으면
 * 편집자가 파이프라인을 못 짠다. 목록에 **비밀값은 실리지 않는다**(결정169) — 이름과
 * 대상만 나간다. 그 전제가 깨지면 이 판단도 다시 해야 한다.
 *
 * **② 실행은 `pipeline.update` 다.** 운영자가 준 축에 '실행' 이 없다. 새 축을 지금
 * 만들지 않고(문서 §7) update 로 묶는다 — 근거는 **읽기만 하는 사람이 파이프라인을
 * 돌려 비용을 쓰게 두면 안 된다**는 것이다. 실행이 편집과 갈려야 할 이유가 생기면
 * 그때 축을 늘린다.
 */
export const ROUTE_PERMISSIONS: Record<string, RoutePermission> = {
  // ── 인증 자체 ──────────────────────────────────────────────────────────────
  'POST /auth/login': { require: NO_PERMISSION, reason: '로그인 전이다 — 권한을 볼 주체가 없다' },
  'POST /auth/logout': { require: NO_PERMISSION, reason: '자기 세션을 닫는 것이다' },
  'GET /auth/me': { require: NO_PERMISSION, reason: '자기 자신을 읽는다' },
  'GET /version': { require: NO_PERMISSION, reason: '빌드 버전. 조직과 무관하다' },
  /*
   * 슬랙 앱 설치 (지시90, 결정1350).
   *
   * 시작은 **admin 이다** — 워크스페이스를 우리 조직에 붙이는 일이고, 그 결과로
   * 자격증명이 저장소에 생긴다. 자격증명을 만드는 다른 문들과 같은 자리에 둔다.
   *
   * 콜백은 `NO_PERMISSION` 이다. 인증 훅 자체를 지나지 않으므로(`auth.ts` 예외)
   * **볼 주체가 없다** — 로그인과 같은 이유다. 거기서 지키는 것은 권한이 아니라
   * `state` 이고, 그 대가를 `auth.ts` 예외 목록에 적어 뒀다.
   */
  'POST /slack/oauth/start': { require: 'admin', reason: '워크스페이스를 붙이면 자격증명이 생긴다' },
  'GET /slack/oauth/callback': {
    require: NO_PERMISSION,
    reason: '슬랙이 부르는 자리다 — 볼 주체가 없다. state 검증이 그 자리의 방벽이다',
  },

  // ── 파이프라인 읽기 ────────────────────────────────────────────────────────
  'GET /graphs': { require: 'pipeline.read', reason: '파이프라인 목록' },
  'GET /graphs/{name}': { require: 'pipeline.read', reason: '그래프 본문' },
  'GET /graphs/{name}/deletion': { require: 'pipeline.read', reason: '지우면 무엇이 사라지나 — 누르기 전에 센다(지시193)' },
  'GET /graphs/{name}/versions': { require: 'pipeline.read', reason: '버전 이력(결정183)' },
  'GET /graphs/{name}/history': {
    require: 'pipeline.read',
    reason: '자동 백업 목록. 남의 이력을 보여 줄 이유가 없다(결정211)',
  },
  'GET /runs': { require: 'pipeline.read', reason: '실행 목록' },
  'GET /stats/pipelines': {
    require: 'pipeline.read',
    reason: '파이프라인 운영 사항(지시191) — 실행 목록을 읽는 사람이 그 합계와 돈 서버를 읽는다. 노드는 인프라라 이름·상태·GPU 만 낸다',
  },
  'GET /stats/dashboard': {
    require: 'pipeline.read',
    reason: 'Dashboard 기술 통계(지시188) — 실행 목록·그래프 목록을 읽을 수 있는 사람이 그 합계도 읽는다',
  },
  'GET /runs/{id}': { require: 'pipeline.read', reason: '실행 상세' },
  'GET /runs/{id}/io': {
    require: 'pipeline.read',
    reason: '프롬프트·출력 전문이다 — 파이프라인을 읽을 수 있으면 그 결과도 읽는다',
  },
  'GET /runs/{id}/steps': { require: 'pipeline.read', reason: '단계별 진행' },
  'GET /runs/{id}/output': {
    require: 'pipeline.read',
    reason: '런이 남긴 산출 — 단계·주고받은 것을 읽는 것과 같은 자리(결정2313)',
  },
  'GET /runs/{id}/files': {
    require: 'pipeline.read',
    reason: '런의 입력·산출에 이미 든 파일이다 — 상세를 읽을 수 있으면 그 파일도 읽는다(지시219)',
  },
  'GET /runs/stream': { require: 'pipeline.read', reason: '실행 스트림(SSE)' },
  /* Database 화면 (지시220) — 표에는 녹음·서식 같은 업무 내용 그 자체가 있다. 파이프라인 읽기로는 안 열린다. */
  'GET /databases': { require: 'access.manage', reason: '등록된 기계마다 SSH 로 붙어 DB 가 있나 잰다' },
  'GET /databases/{id}/catalogs': { require: 'access.manage', reason: '그 서버의 데이터베이스 목록' },
  'GET /databases/{id}/tables': { require: 'access.manage', reason: '표 목록' },
  'GET /databases/{id}/table': { require: 'access.manage', reason: '표의 열·인덱스·줄 수' },
  'GET /databases/{id}/rows': { require: 'access.manage', reason: '표의 내용 — 업무 데이터 그 자체다' },
  'GET /approvals': { require: 'pipeline.read', reason: '대기 중인 승인 목록' },
  'GET /pipelines/{name}/limits': { require: 'pipeline.read', reason: '비용·반복 상한 조회' },
  'GET /deployments': { require: 'pipeline.read', reason: '발행 이력 조회' },

  // 파이프라인을 짜려면 부품 목록이 보여야 한다(위 ①).
  'GET /agent-defs': { require: 'pipeline.read', reason: '편집기 팔레트의 부품' },
  'GET /agent-kinds': { require: 'pipeline.read', reason: '에이전트 종류 선언(결정188)' },
  'GET /tool-defs': { require: 'pipeline.read', reason: '편집기 팔레트의 부품' },
  'GET /models': { require: 'pipeline.read', reason: '고를 수 있는 모델 목록' },
  'GET /datasources': { require: 'pipeline.read', reason: '고를 수 있는 데이터 원천' },
  'GET /environments': { require: 'pipeline.read', reason: '고를 수 있는 환경' },
  'GET /credentials': { require: 'pipeline.read', reason: '이름과 대상만 나간다(위 ①)' },
  'GET /nodes': { require: 'pipeline.read', reason: '실행할 노드를 고르려면 보여야 한다(위 ①)' },
  'GET /deploy-targets': { require: 'pipeline.read', reason: '발행 대상 선택지(위 ①)' },
  'GET /flags/global-stop': {
    require: 'pipeline.read',
    reason: '전면 정지 상태는 모두에게 보여야 한다 — 왜 안 도는지 알 수 없으면 안 된다',
  },

  // ── 파이프라인 만들기 · 고치기 · 지우기 ───────────────────────────────────
  'POST /graphs': { require: 'pipeline.create', reason: '새 파이프라인' },

  // ── 설계 저장소 (지시65, 결정386) ─────────────────────────────────────────
  //
  // 파이프라인과 **같은 축**을 쓴다. 설계는 그 자체로 도는 것이 아니라 그래프에
  // 부어 넣을 조각이고, 부어 넣는 순간 파이프라인이 바뀐다. 축을 새로 만들면
  // "설계는 만들 수 있는데 그것을 적용할 수는 없는" 조합이 생기고, 그 조합에
  // 뜻을 붙일 사람이 없다(docs/07 §7 — 축은 근거가 생길 때 늘린다).
  'GET /designs': { require: 'pipeline.read', reason: '팔레트가 구역을 그리려면 읽어야 한다' },
  'GET /designs/{id}': { require: 'pipeline.read', reason: '설계 본문' },
  'POST /designs': { require: 'pipeline.create', reason: '새 설계를 만든다' },
  'DELETE /designs/{id}': {
    require: 'pipeline.delete',
    reason: '폐기. 소프트 삭제지만 남의 팔레트에서 항목을 없앤다',
  },
  'POST /designs/{id}/apply': {
    require: 'pipeline.update',
    reason:
      '그래프에 모듈을 만든다 — 파이프라인을 고치는 것과 같은 무게다(§15.7). ' +
      '전체 중단·중첩 규칙·감사가 라우트 안에 함께 걸려 있다',
  },
  'PUT /graphs/{name}': { require: 'pipeline.update', reason: '그래프 저장' },
  'DELETE /graphs/{name}': { require: 'pipeline.delete', reason: '파이프라인 지우기 — 고르면 런 이력까지(지시193)' },
  'POST /graphs/{name}/versions': { require: 'pipeline.update', reason: 'Save As(결정183)' },
  'POST /graphs/{name}/publish': {
    require: 'pipeline.update',
    reason: '발행은 파이프라인을 실제로 돌 수 있게 만든다 — 편집과 같은 무게다',
  },
  'POST /graphs/{name}/approve-deploy': {
    require: 'pipeline.update',
    reason:
      '배포 승인은 **자동 배포의 방아쇠**다(지시157) — 이 한 번으로 굽기·master·노드까지 간다. ' +
      '발행과 같은 무게로 둔다',
  },
  'POST /graphs/{name}/eject': { require: 'pipeline.update', reason: '코드 내보내기' },
  'PUT /pipelines/{name}/limits': { require: 'pipeline.update', reason: '비용·반복 상한 변경' },
  'POST /agent-defs': { require: 'pipeline.update', reason: '부품을 늘리는 것도 파이프라인 편집이다' },
  'POST /llm-providers': {
    require: 'access.manage',
    reason: '어디로 요청이 나갈지 정한다 — 실행 대상을 늘리는 것과 같은 자리(POST /nodes)',
  },
  'DELETE /llm-providers/{providerId}': {
    require: 'access.manage',
    reason: '등록과 같은 권한. 빼면 그 공급자로 돌던 것이 멈춘다',
  },
  'PATCH /llm-providers/{providerId}': {
    require: 'access.manage',
    reason: '어느 기계에 사는지를 고친다 — 등록과 같은 자리(POST /llm-providers)',
  },
  'GET /llm-providers': {
    require: 'pipeline.read',
    reason: '무엇으로 돌릴 수 있는지 보는 것 — 모델 목록과 같은 자리',
  },
  'GET /llm-providers/{providerId}/server-models': {
    require: 'pipeline.read',
    reason:
      '무엇으로 돌릴 수 있는지 보는 것 — 목록과 같은 자리. 밖으로 요청이 한 번 나가지만 ' +
      '주소는 관리자가 이미 등록한 것이고 소유자까지 대조한다',
  },
  'PUT /llm-providers/{providerId}/credential': {
    require: 'access.manage',
    reason: '비밀값을 어느 공급자가 쓸지 정한다 — POST /credentials 와 같은 자리',
  },
  'DELETE /models/{providerId}/{model}': {
    require: 'pipeline.update',
    reason: '목록에서 빼는 것도 무엇을 고를 수 있는지 바꾸는 일이다 — 등록과 같은 권한',
  },
  'PUT /models/{providerId}/{model}': {
    require: 'pipeline.update',
    reason: '단가가 틀리면 비용 집계와 일일 상한이 그 위에서 돈다 — 읽기 권한으로 고칠 일이 아니다',
  },
  'POST /agent-defs/infer-schemas': {
    require: 'pipeline.update',
    reason: '정의를 짓는 도중에 부르는 것이고, LLM 을 실제로 호출해 비용이 든다',
  },
  'PUT /agent-defs/{agentDefId}': {
    require: 'pipeline.update',
    reason: '부품을 고치는 것도 파이프라인 편집이다(결정212)',
  },
  'DELETE /agent-defs/{agentDefId}': {
    require: 'pipeline.delete',
    reason: '부품을 지우는 것은 되돌리기 어렵다 — 삭제 권한을 따로 본다',
  },
  'POST /tool-defs': { require: 'pipeline.update', reason: '부품을 늘리는 것도 파이프라인 편집이다' },
  'POST /datasources': { require: 'pipeline.update', reason: '부품을 늘리는 것도 파이프라인 편집이다' },
  /*
   * **고치기·지우기도 같은 권한**이다 (axis-db 청).
   *
   * 등록만 있고 이 둘이 없어서 잘못 만든 데이터소스를 **영원히 못 고쳤다.**
   * 만드는 사람과 고치는 사람이 다를 이유가 없으므로 `POST` 와 같은 축에 둔다 —
   * 지우기를 더 높이면 «만들 수는 있는데 못 치운다» 가 되어 같은 막다른 길이 된다.
   */
  'PATCH /datasources/{id}': { require: 'pipeline.update', reason: '잘못 넣은 config 를 고친다' },
  'DELETE /datasources/{id}': { require: 'pipeline.update', reason: '만든 사람이 치울 수 있어야 한다' },
  // `DELETE /graphs/{name}` 이 아직 없다. 생기면 이 표가 빠졌다고 깨진다.
  'POST /graphs/{name}/revert': {
    require: 'pipeline.update',
    reason: '되돌리기는 파괴적이다 — 읽기 권한으로 할 일이 아니다(지시54 ⑤)',
  },
  'POST /jobs': { require: 'pipeline.update', reason: '실행은 update 다(위 ②)' },
  // OpenAPI 수신기의 문(지시51 ③). 받은 것이 곧 런이 되므로 /jobs 와 같은 자리다.
  'GET /inbound/{path}/runs': { require: 'pipeline.read', reason: '그 API 로 접수된 실행의 목록·결과(지시231 Z1) — 런 읽기와 같은 잣대. 서비스 키는 쌍 검사로 지난다' },
  'GET /inbound/{path}/runs/{runId}': { require: 'pipeline.read', reason: '실행 하나의 결과를 다시 받는다(지시231 Z1) — 소유자·그 API 의 파이프라인 안에서만' },
  'POST /inbound/{path}': { require: 'pipeline.update', reason: '수신도 실행을 만든다 — /jobs 와 같은 잣대' },
  'POST /pipelines/{name}/kill': { require: 'pipeline.update', reason: '실행 중단(위 ②)' },
  'POST /approvals/{runId}': {
    require: 'pipeline.update',
    reason: '승인은 멈춘 실행을 진행시킨다 — 읽기 권한으로 할 일이 아니다(위 ②)',
  },

  // ── 접근 세팅 ──────────────────────────────────────────────────────────────
  'POST /credentials': { require: 'access.manage', reason: '비밀값을 넣는다' },
  /*
   * **폐기는 되돌릴 수 없다** (지시178). 넣는 것과 **같은 무게**로 둔다 —
   * 읽기 권한으로 남의 열쇠를 죽일 수 있으면 안 된다.
   */
  'DELETE /credentials/{credentialId}': {
    require: 'access.manage',
    reason: '자격을 폐기한다 — 되돌릴 수 없다(지시178)',
  },
  'POST /nodes': { require: 'access.manage', reason: '실행 대상을 늘린다' },
  /*
   * 제어 평면이 **어느 기계에 말을 걸지**를 바꾼다 — 실행 대상을 늘리는 것과 같은 무게다
   * (지시169). 잘못 적으면 그 노드로 가는 모든 것이 엉뚱한 기계로 간다.
   */
  'PATCH /nodes/{nodeId}': { require: 'access.manage', reason: '노드 주소를 고친다(지시169)' },
  'DELETE /nodes/{nodeId}': { require: 'access.manage', reason: '함대 목록에서 노드를 치운다(지시173)' },
  /* 읽기다 — 표 이름과 **수**만 나간다(값은 없다). 목록을 읽을 수 있으면 이것도 읽는다. */
  'DELETE /nodes/{nodeId}/abilities/{ability}': {
    require: 'access.manage',
    reason: '능력을 거둔다 — 그 능력이 들고 있던 자격이 풀린다(지시179)',
  },
  'GET /nodes/{nodeId}/bindings': {
    require: 'pipeline.read',
    reason: '무엇이 딸려 죽는지 세어 보인다 — 이름과 수뿐이다(지시179)',
  },
  'PUT /credentials/{credentialId}/uses/{use}': {
    require: 'access.manage',
    reason: '자격이 어느 자리에 쓰일지를 정한다 — 비밀을 만지는 것과 같은 무게다(지시168)',
  },
  'PUT /nodes/{nodeId}/paseo-credential': { require: 'access.manage', reason: '노드 자격증명(지시45)' },
  'DELETE /nodes/{nodeId}/paseo-credential': { require: 'access.manage', reason: '노드 자격증명 폐기' },
  'GET /nodes/{nodeId}/paseo-agents': { require: 'access.manage', reason: '노드 내부를 들여다본다' },
  'GET /nodes/paseo-discovery': { require: 'access.manage', reason: '노드 탐색 결과(지시46)' },
  'PUT /deploy-targets/{id}': { require: 'access.manage', reason: '배포 대상 세팅' },
  'POST /deployments/{id}/rollback': { require: 'access.manage', reason: '되돌리기는 파괴적이다' },
  'GET /fleet/queues': {
    require: 'access.manage',
    reason: '큐 적체 — 어느 잡이 아무도 안 듣는 큐에 앉아 있는지',
  },
  /*
   * **`pipeline.read` 다 — `access.manage` 가 아니다** (지시137).
   *
   * 이 지시가 생긴 까닭이 *"노드 셋이 6일 전 번들로 도는 것을 **아무도 몰랐다**"* 이다.
   * 읽기를 관리자에게만 열면 그 «아무도» 가 그대로 남는다 — 편집자가 *"왜 내 실행이
   * 저 노드에서만 이상한가"* 를 화면에서 알 수 없다.
   *
   * 감출 것도 없다: 커밋 해시와 빌드 시각이고 `GET /v1/version` 이 이미 제 판을 공개한다.
   * **올리는 것**(파괴적 원격 조작)은 별개이고 그쪽이 `access.manage` 를 진다 —
   * 쓰기와 읽기를 가르는 것이 §11 의 기존 잣대다(`/pipelines/{name}/limits` 와 같은 모양).
   */
  'GET /fleet/builds': {
    require: 'pipeline.read',
    reason: '노드가 어느 판으로 도는지 — 안 보이면 6일 낡은 노드를 아무도 모른다(지시137)',
  },
  'GET /provision/targets': { require: 'access.manage', reason: '프로비저닝 대상' },
  'POST /provision/targets': { require: 'access.manage', reason: '프로비저닝 대상 추가' },
  'GET /provision/targets/{id}/node-env-keys': {
    require: 'access.manage',
    /*
     * GPU 조회와 같은 등급이다 — 그 기계에 SSH 로 붙어 파일을 읽는다.
     * ⚠ **값은 안 온다**(이름뿐)지만, **어떤 자격이 그 노드에 서 있는지**는 그 자체로
     *   알아도 되는 사람만 알아야 한다.
     */
    reason: '그 기계의 .env 키 이름 — 기록과 실제가 갈렸는지 재는 유일한 자리',
  },
  'GET /provision/targets/{id}/gpus': {
    require: 'access.manage',
    reason: '그 기계의 GPU 배치 — 남의 장치를 밀어내지 않으려면 알아야 한다',
  },
  'POST /provision/targets/{id}/tailscale': {
    require: 'access.manage',
    // 남의 기계를 우리 tailnet 에 넣는다. 되돌릴 수는 있지만 가벼운 일이 아니다.
    reason: 'tailnet 에 넣는다 — 사람이 승낙한 뒤에만(지시70 ②③)',
  },
  'GET /provision/targets/{id}/tailscale': {
    require: 'access.manage',
    // 읽기지만 그 기계의 sudo 사정과 tailnet 소속이 드러난다 — 조회 권한으로 열지 않는다.
    reason: 'tailnet 에 있나·없으면 넣을 수 있나 — 아무것도 깔지 않는다(지시70 ①)',
  },
  'POST /provision/targets/{id}/postgres': {
    require: 'access.manage',
    // 남의 기계를 바꾼다(사용자 영역이지만 서버가 하나 선다). 승인은 본문에 실린다(결정344).
    reason: 'PostgreSQL 을 깐다 — 사람이 승낙한 뒤에만(지시200)',
  },
  'GET /provision/targets/{id}/postgres': {
    require: 'access.manage',
    // 읽기지만 그 기계의 포트·디스크 사정이 드러난다 — 형제인 docker 판정과 같은 등급.
    reason: '그 기계에 PostgreSQL 이 있나·깔 수 있나 — 아무것도 깔지 않는다(지시200)',
  },
  'POST /provision/targets/{id}/docker': {
    require: 'access.manage',
    // 남의 기계를 바꾼다. 승인 뒤에만 오고, 그 승인은 본문에 실린다(결정344).
    reason: '도커를 깐다 — 사람이 승낙한 뒤에만(지시64 ②③, 지시66)',
  },
  'GET /provision/targets/{id}/docker': {
    require: 'access.manage',
    // 읽기지만 그 기계의 sudo·GPU 사정까지 드러난다 — 조회 권한으로 열지 않는다.
    reason: '그 기계에 도커가 있나·깔 수 있나 — 아무것도 깔지 않는다(지시64 ①)',
  },
  'GET /provision/targets/{id}/speech': {
    require: 'access.manage',
    // 읽기지만 그 기계의 파이썬·CUDA·디스크 사정이 드러난다 — 형제인 docker 판정과 같은 등급.
    reason: '그 기계에 온프렘 음성 서버를 세울 수 있나 — 아무것도 깔지 않는다(지시91 §1)',
  },
  'POST /provision/targets/{id}/speech': {
    require: 'access.manage',
    // 남의 기계에 서비스를 세운다 — 형제인 도커 설치와 같은 등급.
    reason: '온프렘 음성 서버를 세운다(지시91 §1) — 재서 고른 엔진으로만',
  },
  'GET /provision/targets/{id}/containers': {
    require: 'access.manage',
    reason: '그 기계의 우리 컨테이너 목록 — 남의 것은 나오지 않는다',
  },
  'DELETE /provision/targets/{id}': {
    require: 'access.manage',
    reason: '등록한 대상을 치운다 — 기록은 남는다',
  },
  'POST /provision/targets/{id}/probe': {
    require: 'access.manage',
    reason: '그 기계에 실제로 붙어 본다 — 명령은 돌리지 않는다',
  },
  'POST /provision/targets/{id}/apply': { require: 'access.manage', reason: '실제 장비를 바꾼다' },
  'GET /provision/targets/{id}/paseo': {
    require: 'access.manage',
    reason: 'SSH 로 남의 기계 안을 들여다본다(지시46 ①)',
  },
  'POST /provision/targets/{id}/paseo-install': {
    require: 'access.manage',
    reason: '실제 장비에 데몬을 설치하고 비밀번호를 세팅한다(지시46 ②③)',
  },
  /*
   * **읽기인데 `access.manage` 다** — `/fleet/builds` 를 `pipeline.read` 로 연 것과 갈린다.
   *
   * 거기는 커밋 해시라 감출 것이 없었다. 여기는 **비밀 지문을 맞춰 보는 자리**이고,
   * 「어느 자격증명이 그 노드의 것인가」는 그 자체로 알아낼 값이 아니다. 그리고 SSH 로
   * 남의 기계에 붙는다 — 바로 위 `GET …/paseo` 와 같은 잣대다.
   */
  'POST /provision/targets/{id}/paseo-secret/read': {
    require: 'access.manage',
    reason: 'SSH 로 붙어 데몬 비밀의 지문을 맞춰 본다 — 값은 안 옮긴다(지시99 (2)8)',
  },
  'POST /provision/targets/{id}/paseo-secret/reset': {
    require: 'access.manage',
    reason: '원격 환경파일을 바꾸고 **데몬을 재시작한다** — 되돌릴 수 없다(지시99 (2)6·7)',
  },
  /*
   * 플랫폼을 조작하는 MCP 입구(지시56). **여기 요구는 가장 낮은 도구의 것**이고,
   * 변경 도구(지시 전달)는 `mcp-server/tools.ts` 가 다시 확인한다 — 표는 문을,
   * 도구 선언은 그 안의 방을 지킨다. 높은 것을 걸면 읽기만 하려는 클라이언트가 붙지도
   * 못하고, 낮은 것만 걸고 끝내면 변경이 무방비다.
   */
  'POST /mcp': {
    require: 'pipeline.read',
    reason: '도구마다 다시 확인한다 — 여기서는 붙는 것 자체를 막지 않는다(지시56 ①④)',
  },
  'POST /provision/targets/{id}/inference': {
    require: 'access.manage',
    reason: '실제 장비에 추론 서버를 띄우고 프로바이더로 등록한다(지시50 ①②)',
  },
  'GET /provision/runs/{id}': { require: 'access.manage', reason: '프로비저닝 실행 상세' },
  'POST /provision/runs/{id}/revert': { require: 'access.manage', reason: '실제 장비를 되돌린다' },
  /* 장부의 잠금을 연다 — 실행 자체를 만지지는 않지만 **다음 사람이 그 기계에 명령을 걸게** 한다(지시182). */
  'POST /provision/targets/{id}/abandon': {
    require: 'access.manage',
    reason: '매달린 실행을 거둬 그 대상을 다시 열어 준다(지시182)',
  },
  'GET /provision/node-bundle': { require: 'access.manage', reason: '설치 번들 — 노드에 심는 것이다' },
  'GET /api-keys': { require: 'access.manage', reason: '키 목록은 인증 재료다 — 이름도 안 흘린다' },
  'POST /api-keys': { require: 'access.manage', reason: '키 발급' },
  'DELETE /api-keys/{keyId}': { require: 'access.manage', reason: '남의 키 폐기는 서비스 거부다(결정164)' },
  /*
   * 서비스 키(지시228) — **돌릴 수 있는 사람이 제 서비스의 키를 만든다.** 그 키가 하는 일은 web API 를 불러
   * 런을 세우는 것 하나이고, 그것은 이미 `pipeline.update` 가 하는 일이다. `access.manage` 로 올리면
   * 서비스를 만든 사람이 제 키를 못 만들어 운영자에게 매번 손을 벌린다.
   */
  'GET /service-keys': { require: 'pipeline.update', reason: '제 서비스가 쓰는 키의 목록 — 평문은 없다' },
  'POST /service-keys': { require: 'pipeline.update', reason: '서비스 키는 런을 세우는 일만 한다 — 실행과 같은 잣대' },
  'DELETE /service-keys/{keyId}': { require: 'pipeline.update', reason: '폐기는 그 서비스를 멈춘다 — 소유자 안에서만(라우트가 본다)' },
  'POST /flags/global-stop': { require: 'access.manage', reason: '전면 정지는 조직 전체를 멈춘다' },
  'GET /paseo/agents': { require: 'access.manage', reason: '노드 위 에이전트 목록' },
  'POST /paseo/agents': {
    require: 'access.manage',
    // 에이전트 하나는 **자율적으로 명령을 실행하는 프로세스**다. 지시56 과 같은 무게로 본다.
    reason: '자율 실행 프로세스를 새로 띄운다 — 지시56 과 같은 무게다',
  },
  'GET /paseo/{id}/inspect': { require: 'access.manage', reason: '에이전트 내부' },
  'GET /paseo/{id}/logs': { require: 'access.manage', reason: '에이전트 로그' },
  'GET /paseo/{id}/logs/stream': {
    require: 'access.manage',
    // 같은 내용을 흘려 보내는 것이다 — 권한이 낮으면 스트리밍이 우회로가 된다.
    reason: '에이전트 로그(스트리밍) — 서버에서 가린 뒤 내보낸다(지시56 §2-2)',
  },
  'POST /paseo/{id}/send': { require: 'access.manage', reason: '남의 에이전트에 지시할 수 있다(결정168)' },
  'POST /paseo/{id}/stop': { require: 'access.manage', reason: '남의 에이전트를 멈출 수 있다(결정168)' },
  'POST /paseo/{id}/claim': {
    require: 'access.manage',
    // 소유를 주장하는 것이다 — 그 뒤로 그 세션의 프롬프트·CoT 가 열린다(지시56 §2-1).
    reason: '세션 소유를 밝힌다 — 그 뒤 CoT 가 열리므로 가벼운 권한이 아니다',
  },

  // ── 초대 (지시54 ⑥) ───────────────────────────────────────────────────────
  'GET /invites': { require: 'member.manage', reason: '초대 목록. 토큰은 실리지 않는다' },
  'POST /invites': { require: 'member.manage', reason: '조직에 사람을 들이는 열쇠를 만든다' },
  'DELETE /invites/{inviteId}': { require: 'member.manage', reason: '초대 폐기' },
  'POST /invites/accept': {
    require: NO_PERMISSION,
    reason: '아직 계정이 없는 사람이 부른다 — 토큰이 자격증명이고 속도 제한이 걸린다(결정319)',
  },

  // ── 구성원 관리 (지시54 ⑥) ────────────────────────────────────────────────
  'GET /members': { require: 'member.manage', reason: '조직 구성원과 각자의 권한을 본다' },
  'PUT /members/{userId}/permissions': {
    require: 'member.manage',
    reason: '권한 변경. 마지막 admin·자기 승급은 member-policy 가 따로 막는다(결정304)',
  },
  'GET /audit': {
    require: 'member.manage',
    reason:
      '감사는 **누가 무엇을 했나**다 — 조직 운영의 축이라 구성원 관리와 같은 무게로 본다(결정1309). ' +
      'pipeline.read 로 열지 않는다: 파이프라인을 읽는 사람이 남의 행위 이력까지 볼 이유가 없다',
  },
  'GET /members/removed': {
    require: 'member.manage',
    reason: '제거된 사람의 이름·시각이다. 구성원 관리 화면만 본다(결정1307)',
  },
  'POST /members/{userId}/restore': {
    require: 'member.manage',
    reason:
      '조직에 사람을 되돌린다 — 제거와 같은 무게다. **권한은 같이 안 돌아온다**(docs/07 §6-1)',
  },
  'DELETE /members/{userId}': {
    require: 'member.manage',
    reason: '구성원을 뺀다 — admin 을 빼앗는 것과 같아 같은 규칙을 받는다',
  },

  // ── member.manage · admin ─────────────────────────────────────────────────
  // 아직 라우트가 없다. 지시54 ⑥(가입·초대)에서 생긴다. 그때 이 표에 적힌다 —
  // 안 적으면 검사가 깨진다.
};

/** `admin` 은 나머지를 포함한다 — 개별 권한을 일일이 켜 두게 하면 켜는 것을 잊는다. */
export function hasPermission(
  granted: ReadonlySet<Permission>,
  required: Permission | typeof NO_PERMISSION,
): boolean {
  if (required === NO_PERMISSION) return true;
  if (granted.has('admin')) return true;
  return granted.has(required);
}
